/**
 * Create Outbound Dialog Component
 *
 * Dialog for creating new outbound warehouse movements
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { cn } from '../../lib/utils';
import {
  X,
  Trash2,
  Search,
  MapPin,
  History,
  Loader2,
  AlertCircle,
  Check,
  ChevronsUpDown,
  FlaskConical,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../ui/tooltip';
import { Checkbox } from '../ui/checkbox';
import { debounce } from 'lodash';
import warehouseService from '../../services/warehouseService';
import salesService from '../../services/salesService';
import clientService from '../../services/clientService';
import inventoryService from '../../services/inventoryService';
import {
  LocationSelectorDialog,
  InventoryFreshnessIndicator,
  POSourceList,
  InventoryStatusDisplay,
  MultiLocationAllocationDialog,
  ClientInventoryPickerDialog,
  OverrideProductDialog
} from './components';
import { groupInventoryByProduct } from '../../utils/inventoryHelpers';
import InventoryHistoryDialog from '../InventoryHistoryDialog';
import { useDialogDraft } from '../../hooks/useDialogDraft';
import DraftRestoreDialog from '../DraftRestoreDialog';
import { useToast } from '../../hooks/use-toast';

function CreateOutboundDialog({ open, onClose, onSuccess, salesOrderId = null }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sourceType, setSourceType] = useState(salesOrderId ? 'sales_order' : 'manual');
  const [salesOrders, setSalesOrders] = useState([]);
  const [selectedSalesOrder, setSelectedSalesOrder] = useState(null);
  const [clients, setClients] = useState([]);
  const [groupedInventory, setGroupedInventory] = useState({}); // NEW: Grouped by product
  const [productLookupOpen, setProductLookupOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [location, setLocation] = useState('main');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [isSample, setIsSample] = useState(false);

  // Combobox open states
  const [salesOrderComboOpen, setSalesOrderComboOpen] = useState(false);
  const [clientComboOpen, setClientComboOpen] = useState(false);

  // NEW: Location selector dialog state
  const [locationSelectorOpen, setLocationSelectorOpen] = useState(false);
  const [currentProductForLocation, setCurrentProductForLocation] = useState(null);
  const [currentProductIndex, setCurrentProductIndex] = useState(null);

  // NEW: History dialog state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyProductId, setHistoryProductId] = useState(null);
  const [historyLocation, setHistoryLocation] = useState(null);

  // NEW: Multi-location allocation dialog state
  const [allocationDialogOpen, setAllocationDialogOpen] = useState(false);
  const [currentAllocationProduct, setCurrentAllocationProduct] = useState(null);
  const [currentAllocationIndex, setCurrentAllocationIndex] = useState(null);

  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [currentOverrideProduct, setCurrentOverrideProduct] = useState(null);
  const [currentOverrideIndex, setCurrentOverrideIndex] = useState(null);

  // Loading states for autocomplete
  const [loadingSalesOrders, setLoadingSalesOrders] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);

  const { draft, setDraft, clearDraft, hasDraft, isExpired } = useDialogDraft(
    'create_outbound',
    {
      sourceType: salesOrderId ? 'sales_order' : 'manual',
      selectedSalesOrder: null,
      selectedClient: null,
      selectedProducts: [],
      location: 'main',
      deliveryAddress: '',
      notes: '',
      isSample: false,
    },
    { ttlMs: 86400000 }
  );
  const { toast } = useToast();
  const [showDraftRestore, setShowDraftRestore] = useState(false);
  const hasPromptedRef = useRef(false);
  const pendingDraftRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setDraft({
      sourceType,
      selectedSalesOrder,
      selectedClient,
      selectedProducts,
      location,
      deliveryAddress,
      notes,
      isSample,
    });
  }, [open, sourceType, selectedSalesOrder, selectedClient, selectedProducts, location, deliveryAddress, notes, isSample]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) {
      if (hasDraft && !isExpired && !hasPromptedRef.current) {
        pendingDraftRef.current = draft;
        setShowDraftRestore(true);
      }
      hasPromptedRef.current = true;
    } else {
      hasPromptedRef.current = false;
    }
  }, [open, hasDraft, isExpired]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSalesOrder = useCallback(async (soId) => {
    try {
      // 1. Load SO data
      const order = await salesService.getSalesOrder(soId);
      setSelectedSalesOrder(order);
      setSourceType('sales_order');
      setDeliveryAddress(order.delivery_address || '');

      // 2. Extract client and fetch enhanced inventory
      const clientRef = order.client_ref || order.client_id;
      if (!clientRef) {
        // No client - fallback to basic mode
        const outboundProducts = order.products?.map(p => ({
          product_id: p.product_id || p._id,
          product_code: p.product_id,
          product_name: p.name || p.product_name,
          specification: p.specification || '',
          // Manufacturing parameters
          material: p.material || '',
          coating: p.coating || '',
          tip_diameter: p.tip_diameter || '',
          total_length: p.total_length || '',
          degree: p.degree || '',
          shank_diameter: p.shank_diameter || '',
          tolerance: p.tolerance || '',
          blade_type: p.blade_type || '',
          guide_pillar_length: p.guide_pillar_length || '',
          guide_pillar_tolerance: p.guide_pillar_tolerance || '',
          marking: p.marking || '',
          // Quantity and status
          quantity_planned: p.quantity || 0,
          quantity_actual: 0, // Default to 0, set after allocation
          unit: p.unit || '',
          location: 'main',
          locations: [],
          inventoryWarning: 'no_client'
        })) || [];
        setSelectedProducts(outboundProducts);
        return;
      }

      // 3. Extract product codes from SO and batch lookup inventory
      const productCodes = order.products?.map(p => p.product_id).filter(Boolean) || [];
      if (productCodes.length === 0) {
        setSelectedProducts([]);
        return;
      }

      let inventoryLookup = {};
      let frozenStockLookup = {};
      let shippedLookup = {};
      try {
        const progressResult = await salesService.getExecutionProgress(soId);
        if (progressResult?.per_product) {
          Object.entries(progressResult.per_product).forEach(([lineItemId, data]) => {
            if (data.frozen_qty > 0) {
              // Use product_code as key to match batchResult.inventory keys
              frozenStockLookup[data.product_code] = {
                frozen_qty: data.frozen_qty,
                frozen_locations: data.frozen_locations || []
              };
            }
            // Track already-shipped / remaining quantities for partial outbound
            if (data.product_code) {
              shippedLookup[data.product_code] = {
                shipped_qty: data.shipped_qty || 0,
                remaining_qty: data.pending_shipment ?? Math.max(0, (data.required_qty || 0) - (data.shipped_qty || 0))
              };
            }
          });
        }

        // Use batch lookup with global view and location details
        const batchResult = await inventoryService.batchLookupInventory(productCodes, {
          clientId: null,  // Global view for cross-client stock
          includeLocations: true
        });

        Object.entries(batchResult.inventory || {}).forEach(([code, data]) => {
          const frozenInfo = frozenStockLookup[code];
          const frozenLocations = frozenInfo?.frozen_locations || [];

          inventoryLookup[code] = {
            locations: (data.locations || []).map(loc => {
              const frozenLoc = frozenLocations.find(fl =>
                fl.location === loc.location && fl.batch_number === (loc.batch_number || '')
              );
              const frozenQty = frozenLoc?.quantity || 0;

              return {
                location: loc.location,
                batch_number: loc.batch_number || '',
                // loc.reserved_quantity covers ALL pending outbound at this location
                // (this SO's frozen + other orders' frozen/reserved). Subtracting it
                // keeps this SO's own frozen excluded (it ships separately) while
                // also hiding stock frozen for OTHER orders.
                available_quantity: Math.max(0, (loc.quantity || 0) - (loc.reserved_quantity ?? frozenQty)),
                reserved_quantity: loc.reserved_quantity || 0,
                source_client_id: loc.source_client_id,
                source_client_name: loc.source_client_name
              };
            }).filter(loc => loc.available_quantity > 0),
            remaining_quantity: Math.max(0, (data.remaining_quantity || 0) - (frozenInfo?.frozen_qty || 0)),
            available_quantity: Math.max(0, (data.available_quantity || 0) - (frozenInfo?.frozen_qty || 0))
          };
        });

        // Store grouped inventory for LocationSelectorDialog
        const grouped = {};
        Object.entries(inventoryLookup).forEach(([code, inv]) => {
          grouped[code] = {
            product_code: code,
            locations: inv.locations,
            totalAvailable: inv.remaining_quantity
          };
        });
        setGroupedInventory(grouped);
      } catch (invErr) {
        console.warn('Failed to batch lookup inventory:', invErr);
        // Fallback to basic mode
        const outboundProducts = order.products?.map(p => ({
          _id: p._id,
          product_id: p.product_id || p._id,
          product_code: p.product_id,
          product_name: p.name || p.product_name,
          specification: p.specification || '',
          material: p.material || '',
          coating: p.coating || '',
          tip_diameter: p.tip_diameter || '',
          total_length: p.total_length || '',
          degree: p.degree || '',
          shank_diameter: p.shank_diameter || '',
          tolerance: p.tolerance || '',
          blade_type: p.blade_type || '',
          guide_pillar_length: p.guide_pillar_length || '',
          guide_pillar_tolerance: p.guide_pillar_tolerance || '',
          marking: p.marking || '',
          quantity_planned: p.quantity || 0,
          quantity_actual: 0,
          unit: p.unit || '',
          location: 'main',
          locations: [],
          inventoryWarning: 'inventory_fetch_failed'
        })) || [];
        setSelectedProducts(outboundProducts);
        return;
      }

      const outboundProducts = order.products?.map(p => {
        const productCode = p.product_id;
        const inventory = inventoryLookup[productCode];
        const frozenInfo = frozenStockLookup[productCode];

        const baseProduct = {
          product_id: productCode,
          product_code: productCode,
          product_name: p.name || p.product_name,
          specification: p.specification || '',
          // Manufacturing parameters
          material: p.material || '',
          coating: p.coating || '',
          tip_diameter: p.tip_diameter || '',
          total_length: p.total_length || '',
          degree: p.degree || '',
          shank_diameter: p.shank_diameter || '',
          tolerance: p.tolerance || '',
          blade_type: p.blade_type || '',
          guide_pillar_length: p.guide_pillar_length || '',
          guide_pillar_tolerance: p.guide_pillar_tolerance || '',
          marking: p.marking || '',
          // Quantity fields
          quantity_planned: p.quantity || 0,
          quantity_actual: 0, // Default to 0, set after allocation
          unit: p.unit || '',
          shipped_qty: shippedLookup[productCode]?.shipped_qty || 0,
          remaining_qty: shippedLookup[productCode]?.remaining_qty ?? (p.quantity || 0),
          frozen_qty: frozenInfo?.frozen_qty || 0,
          frozen_locations: frozenInfo?.frozen_locations || []
        };

        // No inventory found
        if (!inventory || !inventory.locations || inventory.locations.length === 0) {
          return {
            ...baseProduct,
            location: 'main',
            locations: [],
            inventoryWarning: 'no_inventory',
            requiresLocationSelection: false,
            totalAvailable: 0
          };
        }

        // Filter locations with stock
        const locationsWithStock = inventory.locations.filter(
          loc => loc.available_quantity > 0
        );

        // Calculate total available
        const totalAvailable = inventory.locations.reduce(
          (sum, loc) => sum + (loc.available_quantity || 0), 0
        );

        // No stock anywhere
        if (locationsWithStock.length === 0) {
          return {
            ...baseProduct,
            location: 'main',
            locations: inventory.locations,
            inventoryWarning: 'no_stock',
            requiresLocationSelection: false,
            totalAvailable: 0
          };
        }

        // Initialize with empty allocation - user will allocate manually
        return {
          ...baseProduct,
          locations: inventory.locations,
          locationAllocations: [], // Start with no allocations
          totalAvailable,
          locationsWithStock: locationsWithStock.length,
          requiresAllocation: true, // Flag indicating allocation needed
          // Check if total available is less than planned
          inventoryWarning: totalAvailable < baseProduct.quantity_planned ? 'insufficient_total' : null
        };
      }) || [];

      setSelectedProducts(outboundProducts);
    } catch (err) {
      setError(`Failed to load sales order: ${err.message}`);
      console.error('Error in loadSalesOrder:', err);
    }
  }, []);

  // Load initial data
  useEffect(() => {
    if (open) {
      if (salesOrderId) {
        loadSalesOrder(salesOrderId);
      }
    }
  }, [open, salesOrderId, loadSalesOrder]);

  // Debounced search for sales orders
  const searchSalesOrders = useCallback(async (searchText) => {
    if (!searchText || searchText.length < 2) {
      setSalesOrders([]);
      return;
    }

    try {
      setLoadingSalesOrders(true);
      // Search by sales order ID or client name
      const response = await salesService.getSalesOrders(1, 50, {
        status: 'confirmed',
        sales_order_id: searchText,
        client_name: searchText
      });
      setSalesOrders(response.orders || []);
    } catch (err) {
      console.error('Failed to search sales orders:', err);
      setSalesOrders([]);
    } finally {
      setLoadingSalesOrders(false);
    }
  }, []);

  const debouncedSearchSalesOrders = useMemo(
    () => debounce(searchSalesOrders, 400),
    [searchSalesOrders]
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearchSalesOrders.cancel();
    };
  }, [debouncedSearchSalesOrders]);

  // Debounced search for clients
  const searchClients = useCallback(async (searchText) => {
    if (!searchText || searchText.length < 2) {
      setClients([]);
      return;
    }

    try {
      setLoadingClients(true);
      // Search by client ID or name
      const response = await clientService.getClients(1, 50, 'name', 'asc', {
        client_id: searchText,
        name: searchText
      });
      setClients(response.clients || []);
    } catch (err) {
      console.error('Failed to search clients:', err);
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  }, []);

  const debouncedSearchClients = useMemo(
    () => debounce(searchClients, 400),
    [searchClients]
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearchClients.cancel();
    };
  }, [debouncedSearchClients]);

  // Load initial confirmed sales orders (small set)
  useEffect(() => {
    if (open && sourceType === 'sales_order' && !salesOrderId) {
      const loadInitialSalesOrders = async () => {
        try {
          setLoadingSalesOrders(true);
          const response = await salesService.getSalesOrders(1, 100, { status: 'confirmed' });
          setSalesOrders(response.orders || []);
        } catch (err) {
          console.error('Failed to load initial sales orders:', err);
        } finally {
          setLoadingSalesOrders(false);
        }
      };
      loadInitialSalesOrders();
    }
  }, [open, sourceType, salesOrderId]);

  // Load initial clients (small set)
  useEffect(() => {
    if (open && sourceType === 'manual') {
      const loadInitialClients = async () => {
        try {
          setLoadingClients(true);
          const response = await clientService.getClients(1, 100, 'name', 'asc', {});
          setClients(response.clients || []);
        } catch (err) {
          console.error('Failed to load initial clients:', err);
        } finally {
          setLoadingClients(false);
        }
      };
      loadInitialClients();
    }
  }, [open, sourceType]);

  const handleSourceTypeChange = (value) => {
    setSourceType(value);
    setSelectedSalesOrder(null);
    setSelectedProducts([]);
    setDeliveryAddress('');
  };

  const handleSalesOrderSelect = (order) => {
    setSelectedSalesOrder(order);
    setSalesOrderComboOpen(false);
    if (order) {
      loadSalesOrder(order._id || order.sales_order_id);
    }
  };

  const handleClientSelect = async (client) => {
    setSelectedClient(client);
    setClientComboOpen(false);

    // Load global inventory (cross-client stock is allowed)
    if (client) {
      try {
        // Use global inventory view to show all available stock
        const inventoryResponse = await inventoryService.getInventory(1, 1000, 'name', 'asc', {}, null);

        // Group flat inventory manually for multi-location support
        const grouped = groupInventoryByProduct(inventoryResponse.inventory || []);
        setGroupedInventory(grouped);
      } catch (err) {
        console.error('Failed to load inventory:', err);
        setGroupedInventory({});
      }
    } else {
      setGroupedInventory({});
    }
  };

  const handleOpenProductLookup = () => {
    if (!selectedClient) {
      setError('请先选择客户');
      return;
    }
    setProductLookupOpen(true);
  };

  // Handler for adding products from ClientInventoryPickerDialog
  // Products come with locationAllocations already set
  const handleAddProductsFromPicker = (products) => {
    setSelectedProducts([...selectedProducts, ...products]);
    setProductLookupOpen(false);
  };

  // NEW: Handler for opening location selector
  const handleOpenLocationSelector = (product, index) => {
    const productCode = product.product_code;
    const groupedProduct = groupedInventory[productCode];

    if (groupedProduct) {
      setCurrentProductForLocation(groupedProduct);
      setCurrentProductIndex(index);
      setLocationSelectorOpen(true);
    }
  };

  // NEW: Handler for location selection
  const handleLocationSelect = (selectedLocation) => {
    if (currentProductIndex !== null) {
      const updated = [...selectedProducts];
      updated[currentProductIndex].selectedLocation = selectedLocation.location;
      updated[currentProductIndex].location = selectedLocation.location;
      updated[currentProductIndex].maxAvailable = selectedLocation.available_quantity;
      updated[currentProductIndex].available_quantity = selectedLocation.available_quantity;

      // Reset quantity if it exceeds new location's availability
      if (updated[currentProductIndex].quantity_actual > selectedLocation.available_quantity) {
        updated[currentProductIndex].quantity_actual = selectedLocation.available_quantity;
      }

      setSelectedProducts(updated);
    }

    setLocationSelectorOpen(false);
    setCurrentProductForLocation(null);
    setCurrentProductIndex(null);
  };

  // Handler for viewing inventory history
  const handleViewHistory = (productId, locationName = null) => {
    setHistoryProductId(productId);
    setHistoryLocation(locationName);
    setHistoryDialogOpen(true);
  };

  // Handler for opening multi-location allocation dialog
  const handleOpenAllocationDialog = (product, index) => {
    setCurrentAllocationProduct(product);
    setCurrentAllocationIndex(index);
    setAllocationDialogOpen(true);
  };

  // Handler for confirming allocation
  const handleConfirmAllocation = (updatedProduct) => {
    if (currentAllocationIndex === null || currentAllocationIndex < 0) return;

    const updated = [...selectedProducts];
    updated[currentAllocationIndex] = {
      ...updated[currentAllocationIndex],
      locationAllocations: updatedProduct.locationAllocations,
      totalAllocated: updatedProduct.totalAllocated,
      quantity_actual: updatedProduct.totalAllocated || 0
    };

    setSelectedProducts(updated);
    setAllocationDialogOpen(false);
    setCurrentAllocationProduct(null);
    setCurrentAllocationIndex(null);
  };

  const handleOpenOverrideDialog = (product, index) => {
    if (product.frozen_qty > 0) {
      toast({
        title: '无法替换',
        description: '该产品已冻结库存，无法替换',
        variant: 'destructive'
      });
      return;
    }

    setCurrentOverrideProduct(product);
    setCurrentOverrideIndex(index);
    setOverrideDialogOpen(true);
  };

  const handleConfirmOverride = (replacementProduct) => {
    if (currentOverrideIndex === null || currentOverrideIndex < 0) return;

    const originalProduct = selectedProducts[currentOverrideIndex];
    const updated = [...selectedProducts];

    updated[currentOverrideIndex] = {
      ...replacementProduct,
      quantity_planned: originalProduct.quantity_planned,
      quantity_actual: 0,
      is_override: true,
      original_product_code: originalProduct.product_code,
      original_product_name: originalProduct.product_name,
      original_specification: originalProduct.specification,
      comment: `使用近似库存替代: ${originalProduct.product_code} → ${replacementProduct.product_code}`
    };

    setSelectedProducts(updated);

    const productCodes = [replacementProduct.product_code];
    inventoryService.batchLookupInventory(productCodes, {
      clientId: null,
      includeLocations: true
    }).then(batchResult => {
      const data = batchResult.inventory?.[replacementProduct.product_code];
      if (data) {
        setGroupedInventory(prev => ({
          ...prev,
          [replacementProduct.product_code]: {
            product_code: replacementProduct.product_code,
            locations: (data.locations || []).map(loc => ({
              location: loc.location,
              batch_number: loc.batch_number || '',
              available_quantity: Math.max(0, (loc.quantity || 0) - (loc.reserved_quantity || 0)),
              reserved_quantity: loc.reserved_quantity || 0,
              source_client_id: loc.source_client_id,
              source_client_name: loc.source_client_name
            })),
            totalAvailable: data.remaining_quantity || 0
          }
        }));
      }
    }).catch(err => {
      console.error('Failed to lookup replacement product inventory:', err);
    });

    setOverrideDialogOpen(false);
    setCurrentOverrideProduct(null);
    setCurrentOverrideIndex(null);
  };

  const handleProductChange = (index, field, value) => {
    const updated = [...selectedProducts];
    updated[index][field] = value;
    setSelectedProducts(updated);
  };

  const handleRemoveProduct = (index) => {
    setSelectedProducts(selectedProducts.filter((_, i) => i !== index));
  };

  const handleRestoreDraft = () => {
    const d = pendingDraftRef.current;
    if (!d) return;
    if (d.sourceType) setSourceType(d.sourceType);
    if (d.selectedSalesOrder && !salesOrderId) setSelectedSalesOrder(d.selectedSalesOrder);
    if (d.selectedClient) setSelectedClient(d.selectedClient);
    if (d.selectedProducts) setSelectedProducts(d.selectedProducts);
    if (d.location) setLocation(d.location);
    if (typeof d.deliveryAddress === 'string') setDeliveryAddress(d.deliveryAddress);
    if (typeof d.notes === 'string') setNotes(d.notes);
    if (typeof d.isSample === 'boolean') setIsSample(d.isSample);
    toast({ title: '草稿已恢复' });
    setShowDraftRestore(false);
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setShowDraftRestore(false);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      if (!selectedClient && sourceType === 'manual') {
        throw new Error('请选择客户');
      }

      if (selectedProducts.length === 0) {
        throw new Error('请添加至少一个产品');
      }

      // SO Mode: Validate location allocations and stock availability
      if (sourceType === 'sales_order') {
        // Filter to only products with allocations OR frozen stock (allow partial outbound)
        const productsWithAllocations = selectedProducts.filter(p =>
          (p.locationAllocations && p.locationAllocations.length > 0) ||
          (p.frozen_qty > 0 && p.quantity_actual > 0)
        );

        if (productsWithAllocations.length === 0) {
          throw new Error('请至少为一个产品分配库位');
        }

        // Check for under-allocated products
        const underAllocated = selectedProducts.filter(p => {
          if (!p.locationAllocations || p.locationAllocations.length === 0) return false;
          const totalAllocated = p.locationAllocations.reduce((sum, alloc) => sum + alloc.quantity, 0);
          return totalAllocated < p.quantity_planned;
        });

        if (underAllocated.length > 0) {
          const message = underAllocated.map(p => {
            const totalAllocated = p.locationAllocations.reduce((sum, alloc) => sum + alloc.quantity, 0);
            const frozenInfo = p.frozen_qty > 0 ? ` (其中 ${p.frozen_qty}${p.unit} 已冻结)` : '';
            return `${p.product_name}：需要 ${p.quantity_planned}${p.unit}${frozenInfo}，已分配 ${totalAllocated}${p.unit}`;
          }).join('\n');

          const confirmed = window.confirm(
            `以下产品分配不足：\n\n${message}\n\n注意：已冻结的库存会自动确认，无需手动分配。\n\n是否继续创建出库单？`
          );

          if (!confirmed) {
            setLoading(false);
            return;
          }
        }

        // Check for insufficient stock
        const insufficientStock = selectedProducts.filter(p =>
          p.inventoryWarning === 'insufficient_total' ||
          p.inventoryWarning === 'no_stock'
        );

        if (insufficientStock.length > 0) {
          const message = insufficientStock.map(p => {
            if (p.inventoryWarning === 'no_stock') {
              const frozenInfo = p.frozen_qty > 0 ? ` (但已冻结 ${p.frozen_qty}${p.unit})` : '';
              return `${p.product_name}：无可用库存${frozenInfo}`;
            }
            const frozenInfo = p.frozen_qty > 0 ? ` (已冻结 ${p.frozen_qty}${p.unit})` : '';
            return `${p.product_name}：需要 ${p.quantity_planned}${p.unit}${frozenInfo}，可用 ${p.totalAvailable || 0}${p.unit}`;
          }).join('\n');

          const confirmed = window.confirm(
            `以下产品库存不足：\n\n${message}\n\n注意：已冻结的库存会自动确认出库。\n\n是否继续创建出库单？`
          );

          if (!confirmed) {
            setLoading(false);
            return;
          }
        }
      }

      // Prepare movement data
      let movementData;

      if (sourceType === 'sales_order' && selectedSalesOrder) {
        // Create from sales order with multi-location allocations
        // Only include products that have allocations (filter out unallocated products)
        const productsWithManualAllocations = selectedProducts
          .filter(p => p.locationAllocations && p.locationAllocations.length > 0)
          .flatMap(p => {
            return p.locationAllocations.map(alloc => ({
              product_id: p.product_id,
              product_code: p.product_code,
              product_name: p.product_name,
              specification: p.specification,
              category: p.category,
              quantity_planned: alloc.quantity,
              quantity_actual: alloc.quantity,
              unit: p.unit,
              location: alloc.location,
              source_client_id: alloc.source_client_id,
              source_client_name: alloc.source_client_name,
              ...(p.is_override && {
                is_override: true,
                original_product_code: p.original_product_code,
                original_product_name: p.original_product_name
              })
            }));
          });

        const productsWithFrozenOnly = selectedProducts
          .filter(p => {
            const hasQuantity = (p.quantity_actual || 0) > 0;
            const hasFrozen = (p.frozen_qty || 0) > 0;
            // Include frozen products when:
            // 1. Product has frozen qty
            // 2. Product has quantity to ship
            // 3. Quantity to ship includes the frozen amount (quantity_actual >= frozen_qty)
            return hasFrozen && hasQuantity && (p.quantity_actual >= p.frozen_qty);
          })
          .map(p => ({
            product_id: p.product_id,
            product_code: p.product_code,
            product_name: p.product_name,
            specification: p.specification,
            category: p.category,
            quantity_planned: p.quantity_planned,
            quantity_actual: p.frozen_qty, // Ship exactly the frozen qty
            unit: p.unit,
            location: p.frozen_locations?.[0]?.location || 'main',
            source_client_id: p.frozen_locations?.[0]?.source_client_id,
            source_client_name: p.frozen_locations?.[0]?.source_client_name,
            ...(p.is_override && {
              is_override: true,
              original_product_code: p.original_product_code,
              original_product_name: p.original_product_name
            })
          }));

        const productsWithAllocations = [...productsWithManualAllocations, ...productsWithFrozenOnly];

        const overrideNotes = selectedProducts
          .filter(p => p.is_override)
          .map(p => `${p.original_product_code} → ${p.product_code}`)
          .join('; ');

        const finalNotes = overrideNotes
          ? `${notes}${notes ? '\n' : ''}产品替代记录: ${overrideNotes}`
          : notes;

        const data = {
          sales_order_id: selectedSalesOrder._id || selectedSalesOrder.sales_order_id,
          location: location,
          delivery_address: deliveryAddress,
          notes: finalNotes,
          is_sample: isSample,
          products: productsWithAllocations
        };

        const response = await warehouseService.createOutboundFromSalesOrder(data);
        movementData = response.movement;
      } else {
        // Manual creation - transform products to ensure location is a string
        const productsToSend = selectedProducts
          .filter(p => p.product_id && p.quantity_actual > 0)
          .map(p => {
            const baseProduct = {
              product_id: p.product_id,
              product_code: p.product_code,
              name: p.product_name,
              specification: p.specification,
              category: p.category,
              unit: p.unit
            };

            if (p.is_override) {
              baseProduct.is_override = true;
              baseProduct.original_product_code = p.original_product_code;
              baseProduct.original_product_name = p.original_product_name;
              baseProduct.original_specification = p.original_specification;
              baseProduct.comment = p.comment;
            }

            if (p.locationAllocations && p.locationAllocations.length > 0) {
              return p.locationAllocations.map(alloc => ({
                ...baseProduct,
                quantity: alloc.quantity,
                location: alloc.location,
                source_client_id: alloc.source_client_id,
                source_client_name: alloc.source_client_name
              }));
            }

            return [{
              ...baseProduct,
              quantity: p.quantity_actual,
              location: p.selectedLocation || p.location || location
            }];
          }).flat();

        const overrideNotes = selectedProducts
          .filter(p => p.is_override)
          .map(p => `${p.original_product_code} → ${p.product_code}`)
          .join('; ');

        const finalNotes = overrideNotes
          ? `${notes}${notes ? '\n' : ''}产品替代记录: ${overrideNotes}`
          : notes;

        movementData = {
          movement_type: 'outbound',
          reference_type: 'manual',
          client_id: selectedClient?._id,
          client_ref: selectedClient?._id,
          client_name: selectedClient?.name || selectedClient?.client_name,
          location: location,
          delivery_address: deliveryAddress,
          products: productsToSend,
          notes: finalNotes,
          is_sample: isSample,
          status: 'pending'
        };

        const response = await warehouseService.createMovement(movementData);
        movementData = response.movement;
      }

      // Success
      if (onSuccess) {
        onSuccess(movementData);
      }
      clearDraft();
      handleClose();
    } catch (err) {
      // Surface the backend's error message (e.g. stock validation failures)
      // instead of axios's generic "Request failed with status code 400".
      setError(err.response?.data?.error || err.message || '创建失败');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setSourceType('manual');
      setSelectedSalesOrder(null);
      setSelectedClient(null);
      setSelectedProducts([]);
      setLocation('main');
      setDeliveryAddress('');
      setNotes('');
      setIsSample(false);
      onClose();
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()} data-testid="create-outbound-dialog">
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex flex-row items-center justify-between shrink-0">
          <DialogTitle>创建出库单</DialogTitle>
          <Button variant="ghost" size="icon" onClick={handleClose} disabled={loading}>
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>

        <div className="flex-1 overflow-auto py-4 space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                {error}
                <Button variant="ghost" size="sm" onClick={() => setError(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Source Type Selection */}
            <div className="md:col-span-2 space-y-2">
              <Label>出库类型</Label>
              <Select
                value={sourceType}
                onValueChange={handleSourceTypeChange}
                disabled={loading || !!salesOrderId}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales_order">从销售单创建</SelectItem>
                  <SelectItem value="manual">手动创建</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sales Order Selection */}
            {sourceType === 'sales_order' && (
              <div className="md:col-span-2 space-y-2">
                <Label>搜索销售单 *</Label>
                <Popover open={salesOrderComboOpen} onOpenChange={setSalesOrderComboOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={salesOrderComboOpen}
                      className="w-full justify-between font-normal"
                      disabled={loading || !!salesOrderId}
                    >
                      <div className="flex items-center gap-2">
                        <Search className="h-4 w-4 text-muted-foreground" />
                        {selectedSalesOrder
                          ? `${selectedSalesOrder.sales_order_id || selectedSalesOrder._id} - ${selectedSalesOrder.client_name || ''}`
                          : '输入销售单号或客户名称...'
                        }
                      </div>
                      {loadingSalesOrders ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ChevronsUpDown className="h-4 w-4 opacity-50" />
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput
                        placeholder="搜索销售单..."
                        onValueChange={debouncedSearchSalesOrders}
                      />
                      <CommandList>
                        <CommandEmpty>
                          {loadingSalesOrders ? '搜索中...' : '未找到销售单'}
                        </CommandEmpty>
                        <CommandGroup>
                          {salesOrders.map((order) => (
                            <CommandItem
                              key={order._id}
                              value={order.sales_order_id || order._id}
                              onSelect={() => handleSalesOrderSelect(order)}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedSalesOrder?._id === order._id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {order.sales_order_id || order._id} - {order.client_name || ''}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {/* Client Selection for Manual */}
            {sourceType === 'manual' && (
              <div className="md:col-span-2 space-y-2">
                <Label>搜索客户 *</Label>
                <Popover open={clientComboOpen} onOpenChange={setClientComboOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={clientComboOpen}
                      className="w-full justify-between font-normal"
                      disabled={loading}
                    >
                      <div className="flex items-center gap-2">
                        <Search className="h-4 w-4 text-muted-foreground" />
                        {selectedClient
                          ? `${selectedClient.client_id || ''} - ${selectedClient.name || selectedClient.client_name}`
                          : '输入客户ID或客户名称...'
                        }
                      </div>
                      {loadingClients ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ChevronsUpDown className="h-4 w-4 opacity-50" />
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput
                        placeholder="搜索客户..."
                        onValueChange={debouncedSearchClients}
                      />
                      <CommandList>
                        <CommandEmpty>
                          {loadingClients ? '搜索中...' : '未找到客户'}
                        </CommandEmpty>
                        <CommandGroup>
                          {clients.map((client) => (
                            <CommandItem
                              key={client._id}
                              value={client.name || client.client_name}
                              onSelect={() => handleClientSelect(client)}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedClient?._id === client._id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {client.client_id || ''} - {client.name || client.client_name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {/* Warehouse Location */}
            <div className="space-y-2">
              <Label>仓库位置</Label>
              <Input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                disabled={loading}
              />
            </div>

            {/* Delivery Address */}
            <div className="space-y-2">
              <Label>送货地址</Label>
              <Input
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                disabled={loading}
              />
            </div>

            {/* Sample Checkbox */}
            <div className="space-y-2">
              <Label>样品单</Label>
              <div className="flex items-center gap-2 h-10">
                <Checkbox
                  id="is_sample_outbound"
                  checked={isSample}
                  onCheckedChange={setIsSample}
                  disabled={loading}
                />
                <label htmlFor="is_sample_outbound" className="text-sm text-muted-foreground flex items-center gap-1 cursor-pointer">
                  <FlaskConical className="h-4 w-4 text-purple-500" />
                  标记为样品出库
                </label>
              </div>
            </div>
          </div>

          {/* Products Section */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label>产品明细</Label>
              {sourceType === 'manual' && (
                <Button
                  size="sm"
                  onClick={handleOpenProductLookup}
                  disabled={loading || !selectedClient}
                  className="gap-2"
                >
                  <Search className="h-4 w-4" />
                  选择产品
                </Button>
              )}
            </div>

            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium w-16">#</th>
                      <th className="text-left p-3 font-medium">产品编码</th>
                      <th className="text-left p-3 font-medium">规格</th>
                      {sourceType === 'manual' && <th className="text-left p-3 font-medium">可用库存</th>}
                      {sourceType === 'sales_order' && <th className="text-left p-3 font-medium">库存状态</th>}
                      <th className="text-left p-3 font-medium">订单需求</th>
                      {sourceType === 'sales_order' && <th className="text-left p-3 font-medium">已出库</th>}
                      {sourceType === 'sales_order' && <th className="text-left p-3 font-medium">剩余数量</th>}
                      <th className="text-left p-3 font-medium">本次出库</th>
                      <th className="text-left p-3 font-medium">库位{sourceType === 'sales_order' ? '选择' : ''}</th>
                      {sourceType === 'manual' && <th className="w-12 p-3"></th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {selectedProducts.length === 0 ? (
                      <tr>
                        <td colSpan={sourceType === 'manual' ? 9 : 10} className="text-center p-8 text-muted-foreground">
                          {sourceType === 'sales_order'
                            ? '请选择销售单'
                            : '请点击"选择产品"按钮添加产品'}
                        </td>
                      </tr>
                    ) : (
                      selectedProducts.map((product, index) => (
                        <tr key={index}>
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <span>{index + 1}</span>
                              {isSample && (
                                <FlaskConical className="h-4 w-4 text-purple-500" title="样品" />
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-medium">
                                {product.product_code}
                              </span>
                              {product.is_override && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50 text-xs cursor-help">
                                        已替代
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>原产品: {product.original_product_code} - {product.original_product_name}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          </td>
                          <td className="p-3 text-muted-foreground">
                            {product.specification || '-'}
                          </td>
                          {sourceType === 'manual' && (
                            <td className="p-3">
                              <div>
                                <Badge
                                  className={cn(
                                    "font-semibold",
                                    product.available_quantity > 0
                                      ? "bg-success/100 hover:bg-success"
                                      : "bg-destructive/100 hover:bg-destructive"
                                  )}
                                >
                                  {product.available_quantity || 0}{product.unit || '支'}
                                </Badge>
                                {/* Show location summary for multi-location products */}
                                {product.locations && product.locations.length > 1 && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {product.locations.length} 个位置
                                  </p>
                                )}
                              </div>
                            </td>
                          )}
                          {sourceType === 'sales_order' && (
                            <td className="p-3">
                              <InventoryStatusDisplay product={product} />
                            </td>
                          )}
                          <td className="p-3">
                            <div className="flex flex-col gap-1">
                              <span className="font-medium">{product.quantity_planned}</span>
                              {product.frozen_qty > 0 && (
                                <Badge variant="outline" className="border-blue-400 text-blue-700 bg-blue-50 text-xs w-fit">
                                  其中 {product.frozen_qty} 件已冻结
                                </Badge>
                              )}
                            </div>
                          </td>
                          {sourceType === 'sales_order' && (
                            <td className="p-3">
                              <span className={cn(
                                "font-medium",
                                (product.shipped_qty || 0) > 0 ? "text-green-600" : "text-muted-foreground"
                              )}>
                                {product.shipped_qty || 0}
                              </span>
                            </td>
                          )}
                          {sourceType === 'sales_order' && (
                            <td className="p-3">
                              <Badge
                                variant="outline"
                                className={cn(
                                  "font-semibold",
                                  (product.remaining_qty ?? product.quantity_planned) > 0
                                    ? "border-amber-400 text-amber-700 bg-amber-50"
                                    : "border-green-400 text-green-700 bg-green-50"
                                )}
                              >
                                {product.remaining_qty ?? product.quantity_planned}{product.unit || '支'}
                              </Badge>
                            </td>
                          )}
                          <td className="p-3">
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  value={product.quantity_actual}
                                  onChange={(e) => {
                                    const value = parseFloat(e.target.value) || 0;
                                    if (sourceType === 'manual' && value > product.available_quantity) {
                                      setError(`${product.product_name} 库存不足。可用库存: ${product.available_quantity}`);
                                    } else {
                                      setError(null);
                                    }
                                    handleProductChange(index, 'quantity_actual', value);
                                  }}
                                  disabled={loading}
                                  className={cn(
                                    "w-20",
                                    sourceType === 'manual' && product.quantity_actual > product.available_quantity && "border-destructive",
                                    sourceType === 'sales_order' && product.remaining_qty !== undefined &&
                                      product.quantity_actual > product.remaining_qty && "border-destructive"
                                  )}
                                  min={0}
                                  max={sourceType === 'manual' ? product.available_quantity : undefined}
                                />
                                <span className="text-sm text-muted-foreground">{product.unit || '支'}</span>
                              </div>
                              {sourceType === 'sales_order' && product.remaining_qty !== undefined &&
                                product.quantity_actual > product.remaining_qty && (
                                <p className="text-xs text-destructive">
                                  超出剩余数量 {product.remaining_qty}{product.unit || '支'}，请确认是否重复出库
                                </p>
                              )}
                              {product.frozen_qty > 0 && product.quantity_actual > 0 && (
                                <div className="flex flex-col gap-1">
                                  <div className="text-xs">
                                    <span className="text-muted-foreground">组成: </span>
                                    {product.quantity_actual >= product.frozen_qty ? (
                                      <>
                                        <span className="font-medium text-blue-600">
                                          {Math.min(product.frozen_qty, product.quantity_actual)}{product.unit || '支'} 冻结
                                        </span>
                                        <span className="text-muted-foreground"> + </span>
                                        <span className="font-medium text-green-600">
                                          {product.quantity_actual - product.frozen_qty}{product.unit || '支'} 出库
                                        </span>
                                      </>
                                    ) : (
                                      <span className="font-medium text-blue-600">
                                        {product.quantity_actual}{product.unit || '支'} 全部来自冻结
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      size="sm"
                                      variant={product.quantity_actual === product.quantity_planned ? "default" : "outline"}
                                      className={cn(
                                        "h-6 px-2 text-xs",
                                        product.quantity_actual === product.quantity_planned && "bg-blue-600 hover:bg-blue-700"
                                      )}
                                      onClick={() => {
                                        handleProductChange(index, 'quantity_actual', product.quantity_planned);
                                      }}
                                    >
                                      自动分配 ({product.quantity_planned})
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            {/* SO Mode: Multi-location allocation */}
                            {sourceType === 'sales_order' ? (
                              <div className="space-y-2">
                                {(() => {
                                  const totalAllocated = product.locationAllocations?.reduce((sum, alloc) => sum + (alloc.quantity || 0), 0) || 0;
                                  const targetQty = product.quantity_actual || 0;
                                  const isComplete = totalAllocated >= targetQty && targetQty > 0;
                                  const isPartial = totalAllocated > 0 && totalAllocated < targetQty;

                                  if (targetQty === 0) {
                                    return <p className="text-xs text-muted-foreground">请先填写本次出库数量</p>;
                                  }

                                  return (
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-muted-foreground">已分配:</span>
                                        <span className={cn(
                                          "text-sm font-medium",
                                          isComplete ? "text-green-600" : isPartial ? "text-amber-600" : "text-destructive"
                                        )}>
                                          {totalAllocated} / {targetQty} {product.unit || '支'}
                                        </span>
                                        {isComplete && <span className="text-green-600 text-xs">✓</span>}
                                      </div>
                                      {product.locationAllocations && product.locationAllocations.length > 0 && (
                                        <div className="flex flex-wrap gap-1">
                                          {product.locationAllocations.map((alloc, i) => (
                                            <Badge key={i} variant="secondary" className="text-xs">
                                              {alloc.location}: {alloc.quantity}{product.unit || '支'}
                                            </Badge>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}

                                {/* Allocation Button */}
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant={(() => {
                                      const totalAllocated = product.locationAllocations?.reduce((sum, alloc) => sum + (alloc.quantity || 0), 0) || 0;
                                      const targetQty = product.quantity_actual || 0;
                                      return totalAllocated >= targetQty && targetQty > 0 ? "default" : "outline";
                                    })()}
                                    onClick={() => handleOpenAllocationDialog(product, index)}
                                    className={cn(
                                      "gap-1 text-xs",
                                      (() => {
                                        const totalAllocated = product.locationAllocations?.reduce((sum, alloc) => sum + (alloc.quantity || 0), 0) || 0;
                                        const targetQty = product.quantity_actual || 0;
                                        return totalAllocated >= targetQty && targetQty > 0 ? "bg-green-600 hover:bg-green-700" : "";
                                      })()
                                    )}
                                  >
                                    <MapPin className="h-3 w-3" />
                                    {(() => {
                                      const totalAllocated = product.locationAllocations?.reduce((sum, alloc) => sum + (alloc.quantity || 0), 0) || 0;
                                      const targetQty = product.quantity_actual || 0;
                                      if (totalAllocated >= targetQty && targetQty > 0) return '已分配';
                                      return product.locationAllocations?.length > 0 ? '修改分配' : '分配库位';
                                    })()}
                                  </Button>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleOpenOverrideDialog(product, index)}
                                          disabled={product.frozen_qty > 0}
                                          className="gap-1 text-xs"
                                        >
                                          <Search className="h-3 w-3" />
                                          替换产品
                                        </Button>
                                      </TooltipTrigger>
                                      {product.frozen_qty > 0 && (
                                        <TooltipContent>
                                          <p>已冻结库存，无法替换</p>
                                        </TooltipContent>
                                      )}
                                    </Tooltip>
                                  </TooltipProvider>
                                  {product.product_code && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleViewHistory(product.product_code)}
                                      title="查看库存历史"
                                    >
                                      <History className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ) : (
                              /* Manual Mode: Keep existing location display */
                              product.locations && product.locations.length > 0 ? (
                                <div>
                                  {product.locations.length === 1 ? (
                                    // Single location - simple display with freshness
                                    <div>
                                      <p className="text-sm flex items-center gap-1">
                                        <MapPin className="h-3 w-3" />
                                        {product.locations[0].location}
                                      </p>
                                      {product.locations[0].last_movement && (
                                        <InventoryFreshnessIndicator
                                          timestamp={product.locations[0].last_movement.created_at}
                                          variant="inline"
                                          size="small"
                                        />
                                      )}
                                      {product.locations[0].source_origins && product.locations[0].source_origins.length > 0 && (
                                        <div className="mt-1">
                                          <POSourceList
                                            sources={product.locations[0].source_origins}
                                            maxVisible={1}
                                            size="small"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    // Multiple locations - show selector button
                                    <div>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleOpenLocationSelector(product, index)}
                                        className="gap-1 mb-1"
                                      >
                                        <MapPin className="h-3 w-3" />
                                        {product.selectedLocation || '选择位置'}
                                      </Button>
                                      {product.selectedLocation && product.locations.find(l => l.location === product.selectedLocation) && (
                                        <InventoryFreshnessIndicator
                                          timestamp={product.locations.find(l => l.location === product.selectedLocation)?.last_movement?.created_at}
                                          variant="inline"
                                          size="small"
                                        />
                                      )}
                                    </div>
                                  )}
                                  {/* History button */}
                                  {product.product_code && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleViewHistory(product.product_code, product.selectedLocation || product.location)}
                                      className="mt-1"
                                    >
                                      <History className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              ) : (
                                // Fallback to simple text field
                                <Input
                                  value={product.location || location}
                                  onChange={(e) =>
                                    handleProductChange(index, 'location', e.target.value)
                                  }
                                  disabled={loading}
                                  className="w-24"
                                />
                              )
                            )}
                          </td>
                          {sourceType === 'manual' && (
                            <td className="p-3">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRemoveProduct(index)}
                                disabled={loading}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>备注</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            取消
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || (sourceType === 'sales_order' && !selectedSalesOrder) ||
                     (sourceType === 'manual' && (!selectedClient || selectedProducts.length === 0))}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                创建中...
              </>
            ) : (
              '创建出库单'
            )}
          </Button>
        </DialogFooter>

        {/* Client Inventory Picker Dialog */}
        <ClientInventoryPickerDialog
          open={productLookupOpen}
          onClose={() => setProductLookupOpen(false)}
          onConfirm={handleAddProductsFromPicker}
          clientId={selectedClient?._id}
          clientName={selectedClient?.name || selectedClient?.client_name || ''}
        />

        {/* NEW: Location Selector Dialog */}
        {currentProductForLocation && (
          <LocationSelectorDialog
            open={locationSelectorOpen}
            onClose={() => {
              setLocationSelectorOpen(false);
              setCurrentProductForLocation(null);
              setCurrentProductIndex(null);
            }}
            product={currentProductForLocation}
            onSelect={handleLocationSelect}
            onViewHistory={handleViewHistory}
            selectedLocationName={currentProductForLocation.selectedLocation}
          />
        )}

        {/* NEW: Inventory History Dialog */}
        {historyProductId && (
          <InventoryHistoryDialog
            open={historyDialogOpen}
            onClose={() => {
              setHistoryDialogOpen(false);
              setHistoryProductId(null);
              setHistoryLocation(null);
            }}
            productId={historyProductId}
            clientId={selectedClient?._id}
            initialLocation={historyLocation}
          />
        )}

        {/* NEW: Multi-Location Allocation Dialog */}
        {currentAllocationProduct && (
          <MultiLocationAllocationDialog
            open={allocationDialogOpen}
            onClose={() => {
              setAllocationDialogOpen(false);
              setCurrentAllocationProduct(null);
              setCurrentAllocationIndex(null);
            }}
            product={currentAllocationProduct}
            onConfirm={handleConfirmAllocation}
          />
        )}

        {currentOverrideProduct && (
          <OverrideProductDialog
            open={overrideDialogOpen}
            onClose={() => {
              setOverrideDialogOpen(false);
              setCurrentOverrideProduct(null);
              setCurrentOverrideIndex(null);
            }}
            onConfirm={handleConfirmOverride}
            originalProduct={currentOverrideProduct}
          />
        )}
      </DialogContent>
    </Dialog>
      <DraftRestoreDialog
        open={showDraftRestore}
        onRestore={handleRestoreDraft}
        onDiscard={handleDiscardDraft}
      />
    </>
  );
}

export default CreateOutboundDialog;
